<template>
	<div v-if="isMounted">
		<!-- Bereich Klienten-Stammdaten -->
		<div v-if="client" class="panel m-3">
			<p class="panel-heading">
				Stammdaten
			</p>
			<div class="container pt-3 pb-3">
				<div class="stammdaten">
					<b-image class="stammdaten-avatar" :src="dropFileURL" alt="Profilbild" rounded />
					<div class="stammdaten-facts">
						<p class="title is-5 mb-2">
							{{ factsLine }}
						</p>
						<div class="kv-columns mb-2">
							<div class="kv-grid">
								<span>Geburtstag:</span>
								<span>{{ birthday ? birthday.toLocaleDateString("de-DE") : "-" }}</span>
								<span>Geburtsort:</span>
								<span>{{ birthLocation ? birthLocation : "-" }}</span>
							</div>
							<div class="kv-grid">
								<span>Zugang:</span>
								<span>{{ entry ? entry.toLocaleDateString("de-DE") : "-" }}</span>
								<span>Abgang:</span>
								<span>{{ exit ? exit.toLocaleDateString("de-DE") : "-" }}</span>
							</div>
						</div>
						<b-checkbox v-if="arePicturesAllowed" v-model="arePicturesAllowed"
							:disabled=true>
							Fotoerlaubnis/Veröffentlichung im Internet
						</b-checkbox>
						<p v-else>
							Keine Fotoerlaubnis/Veröffentlichung im Internet
						</p>
						<template v-if="hasContactData">
							<hr class="my-2">
							<div>
								<p v-if="formatPhone(phone1, info1)">
									{{ formatPhone(phone1, info1) }}
								</p>
								<p v-if="formatPhone(phone2, info2)">
									{{ formatPhone(phone2, info2) }}
								</p>
								<p v-if="addressLine">
									{{ addressLine }}
								</p>
							</div>
						</template>
						<template v-if="personRows.length === 0">
							<hr class="my-2">
							<p>Keine Personenzuweisungen erfasst.</p>
						</template>
					</div>
				</div>
			</div>
		</div>

		<!-- Bereich Falldaten -->
		<div class="panel m-3">
			<p class="panel-heading">
				Falldaten
			</p>
			<div v-for="(caseData, index) in sortedCases" :key="caseData.id" class="container pt-3 pb-3">
				<hr v-if="index > 0" style="height: 3px; background-color: black">
				<div class="falldaten">
					<div class="kv-columns mb-2">
						<div class="kv-grid">
							<span>Gruppe:</span>
							<span>
								<a @click="openGroup(caseData.group)">
									{{ caseData.group.attributes.group_name }}
								</a>
							</span>
							<template v-if="caseData.administrativeDistrict">
								<span>Landkreis:</span>
								<span>
									<a @click="openDistrict(caseData.administrativeDistrict)">
										{{ caseData.administrativeDistrict.attributes.name }}
									</a>
								</span>
							</template>
							<span>Gesetzliche Grundlage:</span>
							<span>{{ caseData.statutoryBasis ? caseData.statutoryBasis.attributes.name : "-" }}</span>
							<span>Aktivität:</span>
							<span>
								<span :class="{ 'has-text-weight-bold': caseData.inactive
									? activeCaseCount === 0
									: caseDataArray.length > 1 }">
									{{ caseData.inactive ? "Inaktiv" : "Aktiv" }}
								</span>
								<span v-if="!caseData.inactive && activeCaseCount > 1" class="has-text-danger">
									Mehrere Fälle als aktiv markiert!
								</span>
							</span>
						</div>
						<div v-if="hasLicense(caseData)" class="kv-grid">
							<span>Bewilligungsart:</span>
							<span>{{ caseData.licenseKind ? caseData.licenseKind.attributes.name : "-" }}</span>
							<span>Bewilligung von:</span>
							<span>{{ caseData.licenseStart ? caseData.licenseStart.toLocaleDateString("de-DE") : "-" }}</span>
							<span>Bewilligung bis:</span>
							<span>{{ caseData.licenseEnd ? caseData.licenseEnd.toLocaleDateString("de-DE") : "-" }}</span>
							<template v-if="caseData.licenseFee">
								<span>Entgeld (in €):</span>
								<span>{{ caseData.licenseFee }}</span>
							</template>
						</div>
						<p v-else class="has-text-danger">
							Bewilligung nicht erfasst!
						</p>
					</div>
					<template v-if="caseData.caseDataFileObjekt">
						<hr class="my-2">
						<p class="has-text-weight-bold mb-1">
							Bewilligungsdatei
						</p>
						<div class="kv-grid mb-2">
							<template v-if="caseData.caseDataFile.title">
								<span>Bezeichner:</span>
								<span>{{ caseData.caseDataFile.title }}</span>
							</template>
							<template v-if="caseData.caseDataFile.category?.attributes?.name">
								<span>Kategorie:</span>
								<span>{{ caseData.caseDataFile.category.attributes.name }}</span>
							</template>
							<template v-if="caseData.caseDataFile.documentType">
								<span>Posteingang/Postausgang:</span>
								<span>{{ caseData.caseDataFile.documentType }}</span>
							</template>
							<template v-if="caseData.caseDataFile.date">
								<span>Datum:</span>
								<span>{{ caseData.caseDataFile.date.toLocaleDateString("de-DE") }}</span>
							</template>
							<template v-if="caseData.caseDataFile.creator">
								<span>Ersteller:</span>
								<span>{{ caseData.caseDataFile.creator }}</span>
							</template>
						</div>
						<div class="fileItem">
							<b-icon icon="file-pdf-box" size="is-medium" style="margin-right: 0.25rem;" />
							<a :href="caseData.caseDataFileObjekt.attributes?.pdf?._url" target="_blank">
								{{ caseData.caseDataFileObjekt.attributes?.pdfTitle }}
							</a>
						</div>
					</template>
					<template v-else-if="hasLicense(caseData)">
						<hr class="my-2">
						<p>
							Bewilligungsdatei nicht erfasst.
						</p>
					</template>
				</div>
			</div>
		</div>
		<!-- Bereich Personen -->
		<div v-if="personRows.length > 0" class="panel m-3">
			<p class="panel-heading">
				Personen
			</p>
			<div class="container pt-3 pb-3">
				<b-table class="person-table" :data="personRows" narrowed striped
					hoverable @click="openPerson">
					<b-table-column v-slot="props" label="Name" cell-class="has-text-left">
						{{ props.row.name }}
					</b-table-column>
					<b-table-column v-slot="props" label="Art" cell-class="has-text-left">
						{{ props.row.art }}
					</b-table-column>
					<b-table-column v-slot="props" label="Sorgerecht" cell-class="has-text-left"
						:td-attrs="sorgerechtTdAttrs">
						<b-checkbox v-if="props.row.hasCustody !== undefined"
							:value="props.row.hasCustody" :disabled="true" />
					</b-table-column>
				</b-table>
			</div>
		</div>

		<section class="mx-5 my-2">
			<div class="buttons" style="display: flex">
				<b-button class="is-primary" tag="router-link" to="/clients">
					Zurück
				</b-button>
				<b-button class="is-danger" style="margin-left: auto" @click="isDeleteCheck = true">
					Löschen
				</b-button>
				<b-button class="is-primary" @click="triggerEdit">
					Bearbeiten
				</b-button>
			</div>
		</section>
		<ModalComponent v-if="isDeleteCheck && client" :item="`den Klienten ${firstName} ${lastName}`"
			:is-delete-check="isDeleteCheck" :is-loading="isLoading" :cancel="() => isDeleteCheck = false"
			:submit="submitDelete" />
	</div>
</template>

<script>

/**
 * This file is for viewing a focused client
 */
import Parse from "parse/dist/parse.min.js";
import { queryActiveAndInactiveClients } from "../../lib/Query";
import { getClientPicture } from "../../lib/Picture";
import { negativeFeedBack, positiveFeedBack } from "@/lib/Toast";
import ModalComponent from "@/components/helperComponents/ModalComponent";
import { v4 as uuid } from "uuid";

export default {
	name: "ViewClient",
	components: { ModalComponent },
	data() {
		return {
			client: undefined,
			isDeleteCheck: false,
			arePicturesAllowed: false,
			firstName: "",
			lastName: "",
			birthday: new Date(),
			gender: "",
			isLoading: false,
			entry: new Date(),
			exit: undefined,
			phone1: "",
			phone2: "",
			plz: "",
			location: "",
			street: "",
			dropFile: undefined, //required as value before storing in vuex
			birthLocation: "",
			isMounted: false,
			socialWorker: undefined,
			chosenPersons: [],
			info2: "",
			info1: "",
			caseDataArray: [
				{
					licenseDays: [],
					licenseKind: undefined,
					id: uuid(),
					group: undefined,
					administrativeDistrict: "", //Landkreis
					statutoryBasis: "", //gesetzliche Grundlage
					licenseStart: undefined,
					licenseEnd: undefined,
					licenseFee: "0",
					inactive: false,
					caseDataFileObjekt: undefined,
					caseDataFile: {
						title: undefined,
						category: undefined,
						documentType: "Eingang",
						date: new Date(),
						creator: "",
						pdf: undefined
					}
				},
			],
		};
	},
	computed: {
		age() {
			if (!this.birthday) return undefined;
			const today = new Date();
			let age = today.getFullYear() - this.birthday.getFullYear();
			const monthDiff = today.getMonth() - this.birthday.getMonth();
			if (monthDiff < 0 ||
				(monthDiff === 0 && today.getDate() < this.birthday.getDate()))
			{
				age--;
			}
			return age;
		},
		factsLine() {
			const parts = [`${this.firstName} ${this.lastName}`.trim()];
			if (this.age !== undefined) parts.push(String(this.age));
			if (this.gender) parts.push(this.gender);
			return parts.join(", ");
		},
		addressLine() {
			const city = [this.plz, this.location].filter(Boolean).join(" ");
			return [this.street, city].filter(Boolean).join(", ");
		},
		hasContactData() {
			return !!(this.formatPhone(this.phone1, this.info1) ||
				this.formatPhone(this.phone2, this.info2) || this.addressLine);
		},
		sortedCases() {
			// Active cases first, original order within each group.
			return [ ...this.caseDataArray ]
				.sort((a, b) => (a.inactive ? 1 : 0) - (b.inactive ? 1 : 0));
		},
		activeCaseCount() {
			return this.caseDataArray.filter(c => !c.inactive).length;
		},
		personRows() {
			const rows = [];
			if (this.socialWorker) {
				const { first_name, last_name } = this.socialWorker.attributes;
				rows.push({
					id: this.socialWorker.id,
					name: `${first_name} ${last_name}`,
					art: "Sozialarbeiter",
					hasCustody: undefined,
					person: this.socialWorker
				});
			}
			const personRows = this.chosenPersons.map(entry => {
				const attributes = entry.person.attributes;
				return {
					id: entry.id,
					name: `${attributes.first_name} ${attributes.last_name}`,
					art: attributes.type === "socialWorker"
						? "Sozialarbeiter" : attributes.type,
					hasCustody: !!entry.hasCustody,
					person: entry.person
				};
			});
			// Sorgerecht first, then by name.
			personRows.sort((a, b) => {
				if (a.hasCustody !== b.hasCustody) return a.hasCustody ? -1 : 1;
				return a.name.localeCompare(b.name, "de");
			});
			return rows.concat(personRows);
		},
		dropFileURL() {
			const pictureObject = {
				newProfilePicture: typeof this.dropFile === "string" ? undefined : this.dropFile,
				clientPicture: typeof this.dropFile === "string" ? this.dropFile : undefined,
				clientGender: this.client.attributes.gender
			}
			return getClientPicture(pictureObject)
		},
	},
	async mounted() {
		this.isMounted = true
	},
	async created() {
		let client = this.$route.params.data;
		const clients = await queryActiveAndInactiveClients()
		// Reached via browser navigation (back/forward, reload, deep
		// link): the in-memory object is gone, look it up by the URL id.
		if (!client && this.$route.params.id) {
			client = clients.find(c => c.id === this.$route.params.id);
		}
		if (!client) return await this.$router.push("/clients");
		this.client = client;
		this.$store.dispatch("setDetailTitle",
			`${client.attributes.first_name} ${client.attributes.last_name}`);
		this.caseDataArray = [];
		const focusedClients = clients.filter(
			(c) => c.attributes.uuid === this.client.attributes.uuid
		);

		let profilePictureUrl = undefined;
		focusedClients.forEach((c) => {
			const picture = c.attributes.profile_picture;
			if (picture) profilePictureUrl = picture._url;
		});
		let i = 0
		for (const person of this.client.attributes.persons) {
			this.chosenPersons.push({
				id: uuid(),
				person: person,
				hasCustody: this.client.attributes.custody[i]
			})
			i++
		}

		this.focusedClients = focusedClients;
		for (const client of focusedClients) {
			const {
				first_name,
				last_name,
				street,
				birth_date,
				entry,
				licenseStart,
				licenseEnd,
				exit,
				plz,
				administrative_district,
				location,
				gender,
				statutory_basis,
				phone1,
				phone2,
				group,
				caseDataUuid,
				goal_day,
				socialWorker,
				info1, info2, birth_location, inactive, licenseKind, licenseDays, licenseFile, licenseFee, arePicturesAllowed
			} = client.attributes;
			this.firstName = first_name ? first_name.trim() : "";
			this.lastName = last_name ? last_name.trim() : "";
			this.street = street ? street.trim() : "";
			this.birthday = birth_date ? birth_date : undefined;
			this.birthLocation = birth_location ? birth_location.trim() : undefined;
			this.plz = plz ? plz.trim() : "";
			this.location = location ? location.trim() : "";
			this.gender = gender ? gender.trim() : "";
			this.phone1 = phone1 ? phone1.trim() : "";
			this.phone2 = phone2 ? phone2.trim() : "";
			this.info1 = info1 ? info1.trim() : "";
			this.info2 = info2 ? info2.trim() : "";
			this.goalDay = goal_day ? goal_day : 0;
			this.dropFile = profilePictureUrl;
			this.socialWorker = socialWorker ? socialWorker : undefined
			this.entry = entry ? entry : undefined;
			this.exit = exit ? exit : undefined;//#endregion
			this.arePicturesAllowed = arePicturesAllowed;

			const licenseStartLoc = licenseStart ? licenseStart : undefined;
			const licenseEndLoc = licenseEnd ? licenseEnd : undefined;
			const licenseDaysLoc = licenseDays ? licenseDays : [];
			const licenseFileLoc = licenseFile ? licenseFile : undefined;
			const groupClient = group ? group : undefined;
			const administrativeDistrictClient = administrative_district
				? administrative_district
				: "";
			const statutoryBasisClient = statutory_basis
				? statutory_basis
				: "";
			const inactiveClient = inactive
				? inactive
				: false;
			const licenseKindLoc = licenseKind ? licenseKind : "";
			const licenseFeeLoc = licenseFee ? String(licenseFee.toFixed(2)).replace(".", ",") : "";
			const caseData = {
				id: caseDataUuid,
				group: groupClient,
				administrativeDistrict: administrativeDistrictClient,
				statutoryBasis: statutoryBasisClient,
				licenseStart: licenseStartLoc,
				licenseEnd: licenseEndLoc,
				inactive: inactiveClient,
				licenseKind: licenseKindLoc,
				licenseDays: licenseDaysLoc,
				licenseFee: licenseFeeLoc,
				caseDataFileObjekt: licenseFileLoc,
				caseDataFile: {
					title: licenseFileLoc?.attributes?.title ? licenseFileLoc.attributes.title : undefined,
					category: licenseFileLoc?.attributes?.category ? licenseFileLoc.attributes.category : "",
					documentType: licenseFileLoc?.attributes?.documentType ? licenseFileLoc.attributes.documentType : "",
					date: licenseFileLoc?.attributes?.date ? licenseFileLoc.attributes.date : "",
					creator: licenseFileLoc?.attributes?.creator ? licenseFileLoc.attributes.creator : "",
					pdf: licenseFileLoc?.attributes?.pdf ? licenseFileLoc.attributes.pdf : undefined,
				}
			};
			this.caseDataArray.push(caseData);
			this.isMounted = true;
		}
	},
	methods: {
		formatPhone(phone, info) {
			phone = phone ? phone.trim() : "";
			info = info ? info.trim() : "";
			if (!phone) return "";
			return info ? `${phone} (${info})` : phone;
		},
		hasLicense(caseData) {
			return !!(caseData.licenseKind || caseData.licenseStart ||
				caseData.licenseEnd || caseData.licenseFee);
		},
		openDistrict(district) {
			if (!district) return;
			this.$router.push(
				{ name: "ViewDistrict", params: { id: district.id, data: district } });
		},
		openGroup(group) {
			if (!group) return;
			this.$router.push(
				{ name: "ViewGroup", params: { id: group.id, data: group } });
		},
		sorgerechtTdAttrs(row) {
			// In the mobile card view every cell renders as its own
			// label/value row; drop the Sorgerecht row where it does not
			// apply (the social worker).
			return row.hasCustody === undefined
				? { class: "is-hidden-mobile" } : {};
		},
		openPerson(row) {
			// A click that ends a drag-selection selects text, it should
			// not navigate.
			const selection = window.getSelection && window.getSelection()
			if (selection && selection.toString().length > 0) return
			const name = row.art === "Sozialarbeiter"
				? "ViewSocialWorker"
				: "ViewPerson";
			this.$router.push(
				{ name, params: { id: row.person.id, data: row.person } });
		},
		triggerEdit() {
			this.$router.push({ name: "AddClient", params: { data: this.client } });
		},
		async submitDelete() {
			this.isLoading = true

			try {
				// Alle zusammengehörigen Klienten raussuchen
				const correspondingClientsQuery = new Parse.Query('Client');
				correspondingClientsQuery.equalTo("uuid", this.client.attributes.uuid);
				const correspondingClients = await correspondingClientsQuery.find();

				for (const clientDelete of correspondingClients) {
					clientDelete.set("deleted", true);
					const clientObject = await clientDelete.save();
					const params = { clientId: clientObject.id };
					await Parse.Cloud.run("deleteClientConnections", params)
					const queryNotifications = new Parse.Query("Notification")
					queryNotifications.equalTo("client", clientObject)
					const notifications = await queryNotifications.find()
					for (const notification of notifications) {
						await notification.destroy()
					}
					// 0155 - NumberOfAssociatedClients neu berechnen lassen
					await Parse.Cloud.run("recalculateNumberOfAssociatedClientsByDeletedClientId", params)
				}

				positiveFeedBack("Klient gelöscht!")
				this.isDeleteCheck = false;
				await this.$router.push("/clients");
			}
			catch (e) {
				negativeFeedBack("Klient nicht gelöscht!")
				console.error(e);
			}
			finally {
				this.isLoading = false
			}
		}
	}
}
</script>

<style lang="scss" scoped>
.stammdaten {
	display: flex;
	align-items: flex-start;
	gap: 1.5rem;
	padding-left: 1.5rem;
}

.stammdaten-avatar {
	width: 8rem;
	flex-shrink: 0;
}

/* Crop non-square photos into a circle instead of an ellipse. */
.stammdaten-avatar ::v-deep img {
	width: 8rem;
	height: 8rem;
	object-fit: cover;
}

.stammdaten-facts {
	text-align: left;
	min-width: 0;
}

.stammdaten-facts p {
	margin-bottom: 0.25rem;
}

.person-table ::v-deep tbody tr {
	cursor: pointer;
}

@media screen and (max-width: 768px) {
	.person-table {
		padding: 0 0.75rem;
	}
}

.falldaten {
	padding-left: 1.5rem;
	text-align: left;
}

.kv-grid {
	display: grid;
	/* Value column may shrink so long values wrap, not overflow. */
	grid-template-columns: max-content minmax(0, max-content);
	column-gap: 0.5rem;
	row-gap: 0.25rem;
	min-width: 0;
}

.kv-columns {
	display: flex;
	align-items: flex-start;
	flex-wrap: wrap;
	gap: 0.25rem 3.5rem;
}

.anwesenheitstage {
	flex-direction: column;
}

.fileItem {
	flex-wrap: wrap !important;
	display: flex !important;
	align-items: center !important;
	flex-direction: row !important;
	align-content: center !important;
	justify-content: flex-start !important;
}
</style>